import * as React from 'react'
import moment from 'moment'
import Datepicker from 'react-datepicker'
import { browserHistory } from 'react-router'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { FormError } from '../../../../../../components/FormError'
import { API, IEmployeeHour } from '@getgreenline/homi-shared'
import './EditEmployeeHourModal.scss'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  employeeHour: IEmployeeHour
  onSubmit: () => void
  onClose: () => void
}

interface State {
  startDate: Date
  endDate?: Date
  isSaving: boolean
  errorMessage?: string
}

@inject('currentCompanyStore')
@observer
export class EditEmployeeHourModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      startDate: new Date(props.employeeHour.startTime),
      endDate: props.employeeHour.endTime ? new Date(props.employeeHour.endTime) : undefined,
      isSaving: false,
    }
  }

  save() {
    const company = this.props.currentCompanyStore!.company!
    const hour = this.props.employeeHour
    this.setState({ isSaving: true, errorMessage: undefined })
    API.updateEmployeeHour(
      company.id,
      hour.employeeId,
      hour.id,
      this.state.startDate,
      this.state.endDate!,
    )
      .then(() => {
        this.setState({ isSaving: false })
        this.props.onSubmit()
      })
      .catch((error) => {
        this.setState({
          isSaving: false,
          errorMessage: error.response.data.message || 'There was an error',
        })
      })
  }

  delete() {
    const company = this.props.currentCompanyStore!.company!
    const hour = this.props.employeeHour
    if (window.confirm('Are you sure?')) {
      API.deleteEmployeeHour(company.id, hour.employeeId, hour.id).then(() => {
        this.props.onSubmit()
      })
    }
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const hour = this.props.employeeHour

    let saveButton: JSX.Element
    if (this.state.isSaving) {
      saveButton = (
        <button className='btn btn-success' disabled>
          Saving...
        </button>
      )
    } else if (!this.state.startDate || !this.state.endDate) {
      saveButton = (
        <button className='btn btn-success' disabled>
          Dates required
        </button>
      )
    } else {
      saveButton = (
        <button
          className='btn btn-success'
          onClick={(e) => {
            e.preventDefault()
            this.save()
          }}
        >
          Submit
        </button>
      )
    }

    const body = (
      <div>
        <div className='row'>
          <div className='col-md-6'>
            <div className='form-group'>
              <label className='form-label'>Start time</label>
              <div className='text-muted mb-2'>{moment(this.state.startDate).format('LLLL')}</div>
              <Datepicker
                className='form-control'
                dateFormat='LLL'
                timeFormat='hh:mm a'
                showTimeSelect
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode='select'
                selected={moment(this.state.startDate)}
                timeIntervals={10}
                onChange={(date) => {
                  if (date) {
                    this.setState({ startDate: date.startOf('minute').toDate() })
                  }
                }}
              />
            </div>
          </div>
          <div className='col-md-6'>
            <div className='form-group'>
              <label className='form-label'>End time</label>
              <div className='text-muted mb-2'>{moment(this.state.endDate).format('LLLL')}</div>
              <Datepicker
                className='form-control'
                dateFormat='LLL'
                timeFormat='hh:mm a'
                showTimeSelect
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode='select'
                selected={this.state.endDate ? moment(this.state.endDate) : undefined}
                timeIntervals={10}
                onChange={(date) => {
                  if (date) {
                    this.setState({ endDate: date.startOf('minute').toDate() })
                  }
                }}
              />
            </div>
          </div>
        </div>

        <br />

        <div>
          <div className='d-flex' style={{ justifyContent: 'flex-end' }}>
            <button className='btn btn-danger pull-right mr-2' onClick={() => this.delete()}>
              Delete entry
            </button>
            {saveButton}
          </div>
        </div>
        <FormError message={this.state.errorMessage} />
      </div>
    )

    return (
      <Modal
        isOpen={true}
        toggle={() => this.props.onClose()}
        size='lg'
        id='edit-employee-hour-modal'
      >
        <ModalHeader toggle={() => this.props.onClose()}>
          Edit hours for {hour.employeeName}
        </ModalHeader>
        <ModalBody>{body}</ModalBody>
      </Modal>
    )
  }
}
